import React, { useEffect } from 'react';
import { change as changeFormField, formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { When } from 'react-if';
import { Text } from '@reservamos/elements';
import {
  BILLING_RECIPIENT_TYPES,
  isThirdPartyRecipient,
} from 'utils/purchase/billingDocumentValidation';
import {
  getEnabledBillingCompanyDocuments,
  isBillingCompanyDocumentOrLegacy,
} from 'utils/purchase/billingCompanyDocuments';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import LabelSwitch from '../../../ui/atoms/LabelSwitch';

const purchaserValues = formValueSelector('purchaser');

/**
 * BillingRecipientSection component (Colombia / FlotaOspina invoice flow).
 *
 * The buyer chooses who the invoice is issued to:
 * - "Facturar a mi nombre" (self) → the invoice goes to the passenger; FlotaOspina
 *   uses the passenger document already sent at reserve time.
 * - "Facturar a nombre de un tercero" → the invoice goes to the **buyer (comprador)**,
 *   who must be a third party different from the passenger. The tercero's data is the
 *   comprador form below (name, document, address, city). The organization vs person
 *   case is derived from the comprador's document type (NIT → organization, else →
 *   person), so no separate NIT/razón social fields are needed here.
 *
 * The selection drives the `billingRecipientType` redux-form value that
 * `updatePurchaseFields` forwards to the backend.
 *
 * @returns {React.Component} The billing recipient section.
 */
function BillingRecipientSection() {
  const { t } = useTranslation('passengers');
  const dispatch = useDispatch();
  const features = useWhitelabelFeatures();
  // Resolve the tenant's company-document allowlist so the recipient derivation matches the rest
  // of the checkout (PurchaserFields, updatePurchaseFields, selectIsPurchaserFormValid). Without a
  // configured allowlist this falls back to the legacy NIT behavior.
  const allowedBillingCompanyDocuments = getEnabledBillingCompanyDocuments(
    features.BILLING_COMPANY_ENABLED,
    features.BILLING_COMPANY_AVAILABLE_DOCUMENTS,
  );
  // Subscribe only to the two purchaser fields this section reads. `state.form`
  // is rebuilt by redux-form on every CHANGE/FOCUS/BLUR/TOUCH in any checkout
  // form, so selecting the whole slice (via `useForm`) re-rendered this section
  // on every keystroke. `shallowEqual` keeps it stable until these two change.
  const { billingRecipientType: rawRecipientType, documentType } = useSelector(
    (state) => purchaserValues(state, 'billingRecipientType', 'documentType'),
    shallowEqual,
  );

  const billingRecipientType = rawRecipientType || '';

  const isSelf = billingRecipientType === BILLING_RECIPIENT_TYPES.SELF;
  const isThirdParty = isThirdPartyRecipient(billingRecipientType);

  const recipientForDocumentType = (docType) =>
    isBillingCompanyDocumentOrLegacy(docType, allowedBillingCompanyDocuments)
      ? BILLING_RECIPIENT_TYPES.ORGANIZATION
      : BILLING_RECIPIENT_TYPES.PERSON;

  const setRecipient = (type) =>
    dispatch(changeFormField('purchaser', 'billingRecipientType', type));

  // While billing a third party, keep organization/person in sync with the buyer's
  // document type (NIT → organization, else → person).
  useEffect(() => {
    if (!isThirdParty) return;
    const derived = recipientForDocumentType(documentType);
    if (derived !== billingRecipientType) setRecipient(derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThirdParty, documentType]);

  const onToggleSelf = (event) => {
    setRecipient(event.target.checked ? BILLING_RECIPIENT_TYPES.SELF : '');
  };

  const onToggleThirdParty = (event) => {
    setRecipient(event.target.checked ? recipientForDocumentType(documentType) : '');
  };

  return (
    <>
      <LabelSwitch
        id="bill-to-self"
        checked={isSelf}
        onChange={onToggleSelf}
        label={t('label.bill_to_self')}
      />
      <LabelSwitch
        id="bill-to-third-party"
        checked={isThirdParty}
        onChange={onToggleThirdParty}
        label={t('label.bill_to_third_party')}
      />
      <When condition={isThirdParty}>
        <Text size="S" mateColor="grayStrong">
          {t('label.bill_to_third_party_hint')}
        </Text>
      </When>
    </>
  );
}

export default BillingRecipientSection;
